/* eslint-disable react-refresh/only-export-components -- deliberately a mixed module: shared tokens/helpers exported beside components; HMR full-reload is acceptable here. */
import { ReparoApi } from '@api';
import { getBrandSignatureUrl, getOrgConfig } from '@api/organization';
import ClauseText from '@components/ui/ClauseText';
import QuantityInput from '@components/ui/QuantityInput';
import {
    amountInWords,
    groupTaxByHsn,
    gstStateLabel,
    gstTreatment,
    splitGst,
} from '@diagon-alley/common';
import { Button, Group, NumberInput, Select, Text, TextInput } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { type FC, useMemo, useState } from 'react';

import { LineItemsCsvModal } from './LineItemsCsvModal';
import {
    formatDateDisplay,
    formatMoney,
    type LineDraft,
    type SkuOption,
    StatusBadge,
    textColors,
} from './shared';

/**
 * Heavy presentational form components shared by the PO Builder
 * (procurementOrders) and the Orders (mother-order) authoring surfaces:
 * the live document preview and the line-items editor. Extracted so both
 * forms render an identical, rich authoring experience.
 */

export function getOrganizationIdFromStorage(): string | null {
    try {
        const userJson = localStorage.getItem('user');
        if (!userJson) return null;
        const user = JSON.parse(userJson);
        return user.organizationId || null;
    } catch {
        return null;
    }
}

export async function fetchOrganizationName(
    organizationId: string,
): Promise<string> {
    const data = await ReparoApi<{ name: string }>({
        method: 'GET',
        url: `/organizations/${organizationId}`,
    });
    return data.name;
}

export type PreviewProps = {
    poNumber: string;
    poDate: string;
    expiryDate: string;
    status: string;
    currency: string;
    vendorName: string;
    vendorEmail: string;
    vendorAddress: string;
    vendorGstin: string;
    billToName: string;
    billToAddress: string;
    billToGstin: string;
    shipToName: string;
    shipToEmail: string;
    shipToAddress: string;
    shipToGstin: string;
    /** Place of supply. With the vendor's own state, decides IGST against CGST + SGST. */
    shipToStateCode: string | null;
    contactName: string;
    contactEmail: string;
    lines: LineDraft[];
    taxRate: number;
    subtotal: number;
    taxAmount: number;
    charges: { label: string; amount: number }[];
    total: number;
    notes: string;
    terms: string;
};

function DocHeading({ children }: { children: React.ReactNode }) {
    return (
        <Text
            size="12px"
            fw={700}
            c={textColors.primary}
            mb={5}
            style={{ letterSpacing: '0.03em' }}
        >
            {children}
        </Text>
    );
}

function DocField({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex" style={{ fontSize: 12, lineHeight: 1.65 }}>
            <span style={{ width: 104, flexShrink: 0, color: textColors.secondary }}>
                {label}
            </span>
            <span
                style={{
                    color: value ? textColors.primary : textColors.muted,
                    whiteSpace: 'pre-wrap',
                }}
            >
                {value || '—'}
            </span>
        </div>
    );
}

const lineHasContent = (l: LineDraft): boolean =>
    Boolean(l.description.trim() || l.sku.trim() || l.quantity || l.unitPrice);

export const DocumentPreview: FC<PreviewProps> = (p) => {
    const treatment = gstTreatment(p.vendorGstin, p.shipToStateCode);
    const split = splitGst(p.taxAmount, p.taxRate, treatment);

    /*
     * Per-line rates win the moment any line states one. `groupTaxByHsn` is the same shared
     * roll-up the rendered document uses, collapsed here to one row per rate, so the preview
     * cannot disagree with the PDF about a tax. Orders with no stated line rates keep the
     * flat/split rows they have always shown.
     */
    const rateGroups = new Map<number, number>();

    for (const group of groupTaxByHsn(
        p.lines.map((l) => ({
            quantity: Number(l.quantity) || 0,
            unitPrice: Number(l.unitPrice) || 0,
            gstRate: l.gstRate === '' ? null : Number(l.gstRate),
        })),
    )) {
        rateGroups.set(group.rate, (rateGroups.get(group.rate) ?? 0) + group.tax);
    }

    const perLineTaxRows = [...rateGroups.entries()]
        .sort(([a], [b]) => a - b)
        .flatMap(([rate, tax]) => {
            const groupSplit = splitGst(tax, rate, treatment);

            return groupSplit.treatment === 'INTRASTATE'
                ? [
                      { label: `CGST (${groupSplit.halfRate}%)`, amount: groupSplit.cgst },
                      { label: `SGST (${groupSplit.halfRate}%)`, amount: groupSplit.sgst },
                  ]
                : groupSplit.treatment === 'INTERSTATE'
                  ? [{ label: `IGST (${groupSplit.igstRate}%)`, amount: groupSplit.igst }]
                  : [{ label: `Tax (${rate}%)`, amount: tax }];
        });

    const taxRows = rateGroups.size > 0
        ? perLineTaxRows
        : split.treatment === 'INTRASTATE'
            ? [
                  { label: `CGST (${split.halfRate}%)`, amount: split.cgst },
                  { label: `SGST (${split.halfRate}%)`, amount: split.sgst },
              ]
            : split.treatment === 'INTERSTATE'
              ? [{ label: `IGST (${split.igstRate}%)`, amount: split.igst }]
              : [{ label: `Tax (${p.taxRate}%)`, amount: p.taxAmount }];

    const visibleLines = p.lines.filter(lineHasContent);
    const organizationId = useMemo(() => getOrganizationIdFromStorage(), []);
    const { data: brandOrg } = useQuery({
        queryKey: ['org-config', organizationId],
        queryFn: () => getOrgConfig(organizationId!),
        enabled: !!organizationId,
    });
    const { data: brandSignature } = useQuery({
        queryKey: ['brand-signature-url'],
        queryFn: getBrandSignatureUrl,
        enabled: !!organizationId,
    });

    const { data: organizationName } = useQuery({
        queryKey: ['organization-name', organizationId],
        queryFn: () => fetchOrganizationName(organizationId!),
        enabled: !!organizationId,
        staleTime: Infinity,
    });
    return (
        <div
            style={{
                background: 'white',
                width: '100%',
                maxWidth: 880,
                flex: '1 0 auto',
                padding: 44,
                borderRadius: 4,
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            }}
        >
            <Text
                size="22px"
                fw={700}
                c={textColors.primary}
                style={{ letterSpacing: '0.01em', textTransform: 'capitalize' }}
            >
                {organizationName || ' '}
            </Text>

            <div
                style={{
                    marginTop: 14,
                    borderTop: '1px solid rgb(226, 232, 240)',
                    paddingTop: 16,
                }}
                className="flex items-start justify-between"
            >
                <Text
                    size="14px"
                    fw={700}
                    c={textColors.primary}
                    style={{ letterSpacing: '0.04em' }}
                >
                    PURCHASE ORDER
                </Text>
                <StatusBadge status={p.status} />
            </div>

            <div style={{ marginTop: 16 }} className="grid grid-cols-2 gap-10">
                <div>
                    <DocHeading>PO Details</DocHeading>
                    <DocField label="PO Number" value={p.poNumber || 'Auto-generated'} />
                    <DocField label="Issue Date" value={formatDateDisplay(p.poDate)} />
                    <DocField label="Expiry Date" value={formatDateDisplay(p.expiryDate)} />
                    <DocField label="Currency" value={p.currency} />
                </div>
                <div>
                    <DocHeading>Vendor</DocHeading>
                    <DocField label="Vendor Name" value={p.vendorName} />
                    <DocField label="Email" value={p.vendorEmail} />
                    <DocField label="Address" value={p.vendorAddress} />
                    <DocField label="GST" value={p.vendorGstin} />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-10" style={{ marginTop: 22 }}>
                <div>
                    <DocHeading>Bill To</DocHeading>
                    <DocField label="Business Name" value={p.billToName} />
                    <DocField label="Address" value={p.billToAddress} />
                    <DocField label="GST" value={p.billToGstin} />
                </div>
                <div>
                    <DocHeading>Ship To</DocHeading>
                    <DocField label="Name" value={p.shipToName} />
                    <DocField label="Email" value={p.shipToEmail} />
                    <DocField label="Address" value={p.shipToAddress} />
                    <DocField label="GST" value={p.shipToGstin} />
                    <DocField label="Place of Supply" value={gstStateLabel(p.shipToStateCode) ?? ''} />
                </div>
            </div>

            <div style={{ marginTop: 22 }}>
                <DocHeading>Contact Detail</DocHeading>
                <DocField label="Name" value={p.contactName} />
                <DocField label="Email" value={p.contactEmail} />
            </div>

            <div style={{ marginTop: 26, fontSize: 12 }}>
                <div
                    className="flex"
                    style={{
                        borderBottom: '1.5px solid rgb(226, 232, 240)',
                        color: textColors.secondary,
                        padding: '6px 4px',
                    }}
                >
                    <span style={{ width: 28 }}>#</span>
                    <span style={{ flex: 1 }}>Description</span>
                    <span style={{ width: 56, textAlign: 'right' }}>Qty</span>
                    <span style={{ width: 110, textAlign: 'right' }}>Unit Price</span>
                    <span style={{ width: 118, textAlign: 'right' }}>Total</span>
                </div>
                {visibleLines.length === 0 ? (
                    <div style={{ padding: '12px 4px', color: textColors.muted }}>
                        No line items yet.
                    </div>
                ) : (
                    visibleLines.map((l, idx) => {
                        const lt = (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0);
                        return (
                            <div
                                key={idx}
                                className="flex"
                                style={{
                                    borderBottom: '1px solid rgb(241, 245, 249)',
                                    padding: '6px 4px',
                                }}
                            >
                                <span style={{ width: 28, color: textColors.secondary }}>
                                    {idx + 1}
                                </span>
                                <span style={{ flex: 1, color: textColors.primary }}>
                                    {l.description || '—'}
                                    {l.sku ? (
                                        <span style={{ color: textColors.muted, fontSize: 11, marginLeft: 6 }}>
                                            {l.sku}
                                        </span>
                                    ) : null}
                                    {l.gstRate !== '' && l.gstRate != null ? (
                                        <span style={{ color: textColors.muted, fontSize: 11, marginLeft: 6 }}>
                                            GST {Number(l.gstRate)}%
                                        </span>
                                    ) : null}
                                </span>
                                <span style={{ width: 56, textAlign: 'right' }}>
                                    {Number(l.quantity) || 0}
                                </span>
                                <span style={{ width: 110, textAlign: 'right' }}>
                                    {formatMoney(Number(l.unitPrice) || 0, p.currency)}
                                </span>
                                <span style={{ width: 118, textAlign: 'right' }}>
                                    {formatMoney(lt, p.currency)}
                                </span>
                            </div>
                        );
                    })
                )}
            </div>

            <div className="flex justify-end" style={{ marginTop: 16 }}>
                <div style={{ minWidth: 280 }}>
                    <div className="flex justify-between" style={{ fontSize: 12 }}>
                        <span style={{ color: textColors.secondary }}>Subtotal</span>
                        <span style={{ color: textColors.primary }}>
                            {formatMoney(p.subtotal, p.currency)}
                        </span>
                    </div>
                    {/*
                      * IGST or CGST + SGST, decided by the VENDOR's state against the place of
                      * supply — the buying brand's own state never enters it. Undecidable reads
                      * as one plain "Tax" row, which is what every order without a place of
                      * supply has always shown. Same functions the renderer calls, so the two
                      * cannot disagree about a tax.
                      */}
                    {taxRows.map((row) => (
                        <div
                            key={row.label}
                            className="flex justify-between"
                            style={{ fontSize: 12, marginTop: 4 }}
                        >
                            <span style={{ color: textColors.secondary }}>{row.label}</span>
                            <span style={{ color: textColors.primary }}>
                                {formatMoney(row.amount, p.currency)}
                            </span>
                        </div>
                    ))}
                    {p.charges
                        .filter((c) => c.label.trim() || c.amount)
                        .map((c, idx) => (
                            <div key={idx} className="flex justify-between" style={{ fontSize: 12, marginTop: 4 }}>
                                <span style={{ color: textColors.secondary }}>
                                    {c.label.trim() || 'Additional charge'}
                                </span>
                                <span style={{ color: textColors.primary }}>
                                    {formatMoney(c.amount, p.currency)}
                                </span>
                            </div>
                        ))}
                    <div
                        className="flex justify-between"
                        style={{
                            fontSize: 14,
                            fontWeight: 700,
                            marginTop: 6,
                            paddingTop: 6,
                            borderTop: '1.5px solid rgb(226, 232, 240)',
                            color: textColors.primary,
                        }}
                    >
                        <span>Total</span>
                        <span>{formatMoney(p.total, p.currency)}</span>
                    </div>
                    <div
                        style={{
                            fontSize: 11,
                            color: textColors.secondary,
                            textAlign: 'right',
                            marginTop: 6,
                        }}
                    >
                        Total amount (in words): {amountInWords(p.total, p.currency)}
                    </div>
                </div>
            </div>

            {p.terms ? (
                <div style={{ marginTop: 26 }}>
                    <Text size="12px" fw={700} c={textColors.primary} mb={3}>
                        Terms and Conditions:
                    </Text>
                    {/* The container states the size; ClauseText deliberately has none. */}
                    <div style={{ fontSize: 12 }}>
                        <ClauseText text={p.terms} heading="Terms and Conditions" />
                    </div>
                </div>
            ) : null}
            {p.notes ? (
                <div style={{ marginTop: 16 }}>
                    <Text size="12px" fw={700} c={textColors.primary} mb={3}>
                        Notes:
                    </Text>
                    <div style={{ fontSize: 12 }}>
                        <ClauseText text={p.notes} heading="Notes" />
                    </div>
                </div>
            ) : null}

            {/*
              * The signature block, mirroring the renderer. A facsimile above "Authorised
              * Signatory" — the convention on an Indian commercial document, not a claim to be
              * an electronic signature. Absent entirely when the brand has named no signatory
              * and uploaded no mark.
              */}
            {(brandSignature?.url || brandOrg?.signatoryName) && (
                <div style={{ marginTop: 26, marginLeft: 'auto', width: '40%', minWidth: 200 }}>
                    <Text size="11px" c={textColors.secondary}>
                        For {organizationName || brandOrg?.name || ''}
                    </Text>
                    {brandSignature?.url ? (
                        <img
                            src={brandSignature.url}
                            alt=""
                            style={{
                                display: 'block',
                                maxHeight: 64,
                                maxWidth: 220,
                                objectFit: 'contain',
                                margin: '6px 0',
                            }}
                        />
                    ) : (
                        <div style={{ height: 48 }} />
                    )}
                    {brandOrg?.signatoryName ? (
                        <Text size="11px" fw={600} c={textColors.primary}>
                            {brandOrg.signatoryName}
                        </Text>
                    ) : null}
                    <Text
                        size="10px"
                        c={textColors.secondary}
                        style={{ letterSpacing: '0.04em', textTransform: 'uppercase' }}
                    >
                        {brandOrg?.signatoryDesignation || 'Authorised Signatory'}
                    </Text>
                </div>
            )}
        </div>
    );
};

export const COL = { qty: 58, price: 96, gst: 58, cbm: 64, total: 96, remove: 26 };

export type LineItemsTableProps = {
    lines: LineDraft[];
    /** `disabled` marks a SKU already on the order: greyed in the dropdown, not pickable again. */
    skuData: { value: string; label: string; disabled?: boolean }[];
    currency: string;
    onUpdateLine: (idx: number, patch: Partial<LineDraft>) => void;
    onSkuPicked: (idx: number, sku: string) => void;
    onRemoveLine: (idx: number) => void;
    onAddLine: () => void;
    onClearAll: () => void;
    /** Both present → the footer offers bulk-add via CSV. */
    skuOptions?: SkuOption[];
    onImportLines?: (lines: LineDraft[]) => void;
    /** When a search matches nothing. The default assumes the list is the whole catalogue. */
    nothingFoundMessage?: string;
    /** The CSV import's reason for a SKU it cannot resolve. Defaults to "not in catalog". */
    csvUnmatchedReason?: string;
    /**
     * A line's CBM (quantity × the product's per-unit volume), or null when the product has
     * none on file. Present only for brands measured by volume — the column appears with it.
     */
    cbmOf?: (line: LineDraft) => number | null;
    /**
     * Show a Ready-on date per line. Mother orders track when each line's goods will be
     * ready at the vendor; a plain purchase order has no such column.
     */
    readiness?: boolean;
};

export const LineItemsTable: FC<LineItemsTableProps> = ({
    lines,
    skuData,
    currency,
    onUpdateLine,
    onSkuPicked,
    onRemoveLine,
    onAddLine,
    onClearAll,
    skuOptions,
    onImportLines,
    nothingFoundMessage,
    csvUnmatchedReason,
    cbmOf,
    readiness,
}) => {
    const [csvOpen, setCsvOpen] = useState(false);
    /*
     * The case size per SKU on offer, so a line can be typed in cases. The column only widens
     * to fit the Units | Cases toggle when some product actually has one: a brand that has
     * never heard of cases keeps the narrow box it always had.
     */
    const unitsPerCaseBySku = useMemo(
        () =>
            new Map(
                (skuOptions ?? [])
                    .filter((option) => option.unitsPerCase)
                    .map((option) => [option.sku, option.unitsPerCase as number]),
            ),
        [skuOptions],
    );
    const qtyWidth = unitsPerCaseBySku.size > 0 ? 190 : COL.qty;
    return (
        <>
            <div style={{ border: '1px solid rgb(226, 232, 240)', borderRadius: 6, overflow: 'hidden' }}>
                <div
                    className="flex items-center"
                    style={{
                        background: 'rgb(248, 250, 252)',
                        borderBottom: '1px solid rgb(226, 232, 240)',
                        padding: '8px 10px',
                        gap: 8,
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: '0.05em',
                        color: textColors.secondary,
                    }}
                >
                    <span style={{ flex: 2 }}>SKU</span>
                    <span style={{ flex: 2.4 }}>ITEM</span>
                    <span style={{ width: qtyWidth, textAlign: 'right' }}>QTY</span>
                    <span style={{ width: COL.price, textAlign: 'right' }}>UNIT PRICE</span>
                    <span style={{ width: COL.gst, textAlign: 'right' }}>GST %</span>
                    {readiness ? <span style={{ width: 130 }}>READY ON</span> : null}
                    {cbmOf ? <span style={{ width: COL.cbm, textAlign: 'right' }}>CBM</span> : null}
                    <span style={{ width: COL.total, textAlign: 'right' }}>TOTAL</span>
                    <span style={{ width: COL.remove }} />
                </div>

                {lines.map((line, idx) => {
                    const lineTotal = (Number(line.quantity) || 0) * (Number(line.unitPrice) || 0);
                    const lineCbm = cbmOf ? cbmOf(line) : undefined;
                    return (
                        <div
                            key={idx}
                            // Top-aligned: the quantity cell grows a caption ("72 per case") under
                            // its box, and centring would lift that box out of line with the rest.
                            className="flex items-start"
                            style={{ padding: '6px 10px', gap: 8, borderBottom: '1px solid rgb(241, 245, 249)' }}
                        >
                            {/*
                              * The pick comes first and the description follows it: the SKU is
                              * what decides the line, and the description autofills off it (and
                              * stays editable for what the document should say).
                              */}
                            <div style={{ flex: 2 }}>
                                <Select
                                    placeholder="Pick from catalog"
                                    data={skuData}
                                    value={line.sku || null}
                                    onChange={(val) => {
                                        if (val) onSkuPicked(idx, val);
                                        else
                                            onUpdateLine(idx, {
                                                sku: '',
                                                materialId: null,
                                                productId: null,
                                                description: '',
                                                gstRate: '',
                                            });
                                    }}
                                    searchable
                                    clearable
                                    nothingFoundMessage={nothingFoundMessage ?? 'Add this SKU to your catalog first'}
                                    // The hint lives in the dropdown only: the label itself is what the
                                    // box echoes once a SKU is picked, and must stay clean there.
                                    renderOption={({ option }) => (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, width: '100%' }}>
                                            <span>{option.label}</span>
                                            {option.disabled ? (
                                                <span style={{ fontSize: 11, color: textColors.muted, whiteSpace: 'nowrap' }}>on this order</span>
                                            ) : null}
                                        </div>
                                    )}
                                    size="xs"
                                    limit={25}
                                    // Portalled and widened: the row scrolls inside a modal, and a
                                    // dropdown clipped by that scroll container showed two options
                                    // of a 400-SKU catalogue. A product name plus its code needs
                                    // the room.
                                    comboboxProps={{
                                        withinPortal: true,
                                        zIndex: 10001,
                                        width: 440,
                                        position: 'bottom-start',
                                    }}
                                />
                            </div>
                            <div style={{ flex: 2.4 }}>
                                <TextInput
                                    placeholder="Item description"
                                    value={line.description}
                                    onChange={(e) => onUpdateLine(idx, { description: e.currentTarget.value })}
                                    size="xs"
                                />
                            </div>
                            <div style={{ width: qtyWidth }}>
                                <QuantityInput
                                    value={line.quantity}
                                    onChange={(quantity) => onUpdateLine(idx, { quantity })}
                                    unitsPerCase={unitsPerCaseBySku.get(line.sku) ?? null}
                                    min={0}
                                    size="xs"
                                    hideControls
                                />
                            </div>
                            <div style={{ width: COL.price }}>
                                <NumberInput
                                    value={line.unitPrice}
                                    title={line.priceDetail}
                                    onChange={(val) =>
                                        // A typed price is the user's: it stops following the agreed prices.
                                        onUpdateLine(idx, {
                                            unitPrice: typeof val === 'number' ? val : '',
                                            priceAuto: false,
                                            priceNote: undefined,
                                            priceWarn: false,
                                            priceDetail: undefined,
                                        })
                                    }
                                    min={0}
                                    size="xs"
                                    hideControls
                                />
                                {line.noAgreedPrice && !line.priceNote && (
                                    <Text size="10px" c={textColors.muted} title="This vendor has no agreed price on file for this item">
                                        No agreed price
                                    </Text>
                                )}
                                {line.priceNote && (
                                    <Text
                                        size="10px"
                                        c={line.priceWarn ? 'orange.7' : textColors.muted}
                                        title={line.priceWarn ? "Under the vendor's minimum order quantity for this item; priced at the lowest tier" : 'The vendor has no price for this item in the order currency'}
                                    >
                                        {line.priceNote}
                                    </Text>
                                )}
                            </div>
                            <div style={{ width: COL.gst }}>
                                <NumberInput
                                    value={line.gstRate}
                                    onChange={(val) =>
                                        onUpdateLine(idx, { gstRate: typeof val === 'number' ? val : '' })
                                    }
                                    min={0}
                                    max={28}
                                    size="xs"
                                    hideControls
                                />
                            </div>
                            {readiness ? (
                                <div style={{ width: 130 }}>
                                    <TextInput
                                        type="date"
                                        value={line.expectedReadinessDate ?? ''}
                                        onChange={(e) =>
                                            onUpdateLine(idx, { expectedReadinessDate: e.currentTarget.value })
                                        }
                                        size="xs"
                                        title="When this line's goods will be ready at the vendor. Optional now; set it later on the order."
                                    />
                                </div>
                            ) : null}
                            {cbmOf ? (
                                <div
                                    style={{
                                        width: COL.cbm,
                                        textAlign: 'right',
                                        fontSize: 12,
                                        paddingTop: 6,
                                        fontVariantNumeric: 'tabular-nums',
                                        // Amber, not blank: a line with no CBM is a data gap somebody
                                        // has to see, not a zero that quietly shrinks the total.
                                        color: line.sku && lineCbm == null ? 'rgb(180, 83, 9)' : textColors.secondary,
                                    }}
                                    title={line.sku && lineCbm == null ? 'No CBM on this product' : undefined}
                                >
                                    {!line.sku ? '' : lineCbm == null ? '—' : lineCbm.toFixed(2)}
                                </div>
                            ) : null}
                            <div
                                style={{
                                    width: COL.total,
                                    textAlign: 'right',
                                    fontSize: 12,
                                    paddingTop: 6,
                                    fontWeight: 600,
                                    color: textColors.primary,
                                }}
                            >
                                {formatMoney(lineTotal, currency)}
                            </div>
                            <button
                                type="button"
                                onClick={() => onRemoveLine(idx)}
                                disabled={lines.length === 1}
                                style={{
                                    width: COL.remove,
                                    // Centred on the 30px input line now that the row top-aligns.
                                    height: 30,
                                    border: 'none',
                                    background: 'transparent',
                                    color: lines.length === 1 ? textColors.muted : 'rgb(205, 101, 101)',
                                    cursor: lines.length === 1 ? 'not-allowed' : 'pointer',
                                    fontSize: 13,
                                }}
                            >
                                ✕
                            </button>
                        </div>
                    );
                })}
            </div>

            <Group justify="space-between" mt={8}>
                <Button variant="subtle" color="red" size="xs" onClick={onClearAll}>
                    Clear all
                </Button>
                <Group gap={4}>
                    {skuOptions && onImportLines ? (
                        <Button variant="subtle" size="xs" onClick={() => setCsvOpen(true)}>
                            Upload CSV
                        </Button>
                    ) : null}
                    <Button variant="subtle" size="xs" onClick={onAddLine}>
                        + Add line
                    </Button>
                </Group>
            </Group>

            {skuOptions && onImportLines ? (
                <LineItemsCsvModal
                    opened={csvOpen}
                    onClose={() => setCsvOpen(false)}
                    skuOptions={skuOptions}
                    unmatchedReason={csvUnmatchedReason}
                    onImport={onImportLines}
                />
            ) : null}
        </>
    );
};
